import React, { useState } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CloseIcon from '@mui/icons-material/Close';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { format, isValid, parse } from 'date-fns';
import { useSnackbar } from 'notistack';
import { CheckInTypes } from '../checkInData';
import { submitCheckInDetails } from '../checkInService';
import PrimaryActionButton from './PrimaryActionButton';

const durationCheckInTypes = ['Weekly', 'Daily'];
const rentCollectionDays = Array.from({ length: 28 }, (_, index) => index + 1);
const checkInDateDisplayFormat = 'dd/MM/yyyy';

function formatAmount(value) {
  if(value === null || value === undefined || value === ''){
    return '0.00';
  }

  const numberValue = Number(value);
  if(Number.isNaN(numberValue)){
    return value;
  }

  return numberValue.toFixed(2);
}

function getDurationLabel(checkInType) {
  if(checkInType === 'Weekly'){
    return 'Number of Weeks';
  }

  if(checkInType === 'Daily'){
    return 'Number of Days';
  }

  return '';
}

function getDurationMax(checkInType) {
  if(checkInType === 'Weekly'){
    return 4;
  }

  if(checkInType === 'Daily'){
    return 30;
  }

  return null;
}

function normalizeDurationValue(value, checkInType) {
  const numericValue = value.replace(/\D/g, '');
  const maxValue = getDurationMax(checkInType);

  if(!numericValue || !maxValue){
    return numericValue;
  }

  return String(Math.min(Number(numericValue), maxValue));
}

function shouldShowRentCollectionDay(rentCollectionType, checkInType) {
  return String(rentCollectionType || '').toLowerCase() === 'custom'
    && String(checkInType || '').toLowerCase() === 'monthly';
}

function parseCheckInDate(value) {
  if(!value){
    return null;
  }

  const parsedDate = parse(value, 'yyyy-MM-dd', new Date());
  return isValid(parsedDate) ? parsedDate : null;
}

function getSharingTypeValue(value, sharingType) {
  if(value === null || value === undefined){
    return '';
  }

  if(typeof value === 'object'){
    return value[sharingType] ?? value[String(sharingType)] ?? value[Number(sharingType)] ?? '';
  }

  return value;
}

function getBookingRoomLabel(bookingDetails = {}) {
  return bookingDetails.bedInfo || 'the booked room';
}

function toApiNumber(value) {
  const numberValue = Number(value);
  return Number.isNaN(numberValue) ? value : numberValue;
}

function isCustomRentCollection(rentCollectionType) {
  return String(rentCollectionType || '').toLowerCase() === 'custom';
}

function isMonthlyCheckIn(checkInType) {
  return String(checkInType || '').toLowerCase() === 'monthly';
}

function formatCheckInDateForApi(checkInDate) {
  const value = String(checkInDate || '');
  const parsedDate = parse(value, 'yyyy-MM-dd', new Date());

  if(!isValid(parsedDate)){
    return value;
  }

  return format(parsedDate, 'dd-MM-yyyy');
}

function getSelectedBedId(selectedRoom, bookingDetails) {
  if(bookingDetails){
    return bookingDetails.bedId ?? 0;
  }

  return selectedRoom?.availableBeds?.[0]?.bedId ?? 0;
}

function buildCheckInPayload({ tenantUuid, selectedRoom, bookingDetails, sharingType, checkInDetails, rentCollectionType }) {
  const rentCollectionDay = isCustomRentCollection(rentCollectionType) && isMonthlyCheckIn(checkInDetails.checkInType)
    ? Number(checkInDetails.rentCollectionDay || 0)
    : 0;
  const effectiveSharingType = bookingDetails?.sharingType ?? sharingType;

  return {
    tenantUuid,
    checkInDate: formatCheckInDateForApi(checkInDetails.checkInDate),
    sharingType: toApiNumber(effectiveSharingType),
    bedId: getSelectedBedId(selectedRoom, bookingDetails),
    checkInType: checkInDetails.checkInType,
    rentCollectionDay,
    daysWeeksCount: Number(checkInDetails.numberOfWeeksDays || 0),
  };
}

function ConfirmCheckInDialog({
  open,
  pgId,
  tenantUuid,
  selectedRoom,
  checkInDetails,
  sharingType,
  rentCollectionType,
  onClose,
  onDetailsChange,
  onSubmitSuccess,
  pgDefaults,
  bookingDetails,
  title = 'Confirm Check-In Details',
  proceedLabel,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const currentCheckInDetails = checkInDetails || {};
  const isBookingMode = Boolean(bookingDetails);
  const shouldShowDuration = durationCheckInTypes.includes(currentCheckInDetails.checkInType);
  const shouldShowRentDay = shouldShowRentCollectionDay(rentCollectionType, currentCheckInDetails.checkInType);
  const durationLabel = getDurationLabel(currentCheckInDetails.checkInType);
  const durationMax = getDurationMax(currentCheckInDetails.checkInType);
  const isCheckInDateValid = Boolean(currentCheckInDetails.checkInDate);
  const isCheckInTypeValid = Boolean(currentCheckInDetails.checkInType);
  const isRentDayValid = !shouldShowRentDay || Boolean(currentCheckInDetails.rentCollectionDay);
  const isDurationValid = !shouldShowDuration || currentCheckInDetails.numberOfWeeksDays !== '';
  const isProceedDisabled = isSubmitting || !isCheckInDateValid || !isCheckInTypeValid || !isRentDayValid || !isDurationValid;
  const checkInDateValue = parseCheckInDate(currentCheckInDetails.checkInDate);

  const updateField = (field) => (event) => {
    onDetailsChange?.({
      ...currentCheckInDetails,
      [field]: event.target.value,
    });
  };

  const updateCheckInType = (event) => {
    const value = event.target.value;

    onDetailsChange?.({
      ...currentCheckInDetails,
      checkInType: value,
      rentCollectionDay: shouldShowRentCollectionDay(rentCollectionType, value)
        ? currentCheckInDetails.rentCollectionDay
        : '',
      numberOfWeeksDays: durationCheckInTypes.includes(value)
        ? normalizeDurationValue(currentCheckInDetails.numberOfWeeksDays || '', value)
        : '',
    });
  };

  const updateNumberOfWeeksDays = (event) => {
    onDetailsChange?.({
      ...currentCheckInDetails,
      numberOfWeeksDays: normalizeDurationValue(event.target.value, currentCheckInDetails.checkInType),
    });
  };

  const updateCheckInDate = (newValue) => {
    onDetailsChange?.({
      ...currentCheckInDetails,
      checkInDate: newValue && isValid(newValue) ? format(newValue, 'yyyy-MM-dd') : '',
    });
  };
  const { maintenance, rent, advance, advanceSameAsRent } = pgDefaults || {};
  const roomRent = getSharingTypeValue(rent, sharingType);
  const securityDeposit = advanceSameAsRent ? roomRent : getSharingTypeValue(advance, sharingType);
  const fallbackRentMessage = 'Please discuss rent and security deposit details with the property owner as these will be set during approval.';
  const rentDetailsMessage = roomRent ? (
    <>
      Expected monthly rent for this room is <strong>Rs.{formatAmount(roomRent)}</strong>, security deposit is <strong>Rs.{formatAmount(securityDeposit)}</strong>, maintenance is <strong>Rs.{formatAmount(maintenance)}</strong>, this can be changed by the property owner during the approval.
    </>
  ) : fallbackRentMessage;

  const submitCheckIn = () => {
    if(!tenantUuid){
      enqueueSnackbar('Tenant details are missing. Please complete profile details first.', {variant: 'error'});
      return;
    }

    if(!getSelectedBedId(selectedRoom, bookingDetails)){
      enqueueSnackbar(
        isBookingMode
          ? 'Booking bed details are missing. Please contact the property owner.'
          : 'No available bed found for the selected room',
        {variant: 'error'},
      );
      return;
    }

    const body = buildCheckInPayload({
      tenantUuid,
      selectedRoom,
      bookingDetails,
      sharingType,
      checkInDetails: currentCheckInDetails,
      rentCollectionType,
    });

    setIsSubmitting(true);

    submitCheckInDetails(pgId, body).then(({error}) => {
      setIsSubmitting(false);

      if(error){
        enqueueSnackbar(error?.message || 'Unable to submit check-in details', {variant: 'error'});
        return;
      }

      enqueueSnackbar('Check-in details submitted successfully', {variant: 'success'});
      onSubmitSuccess?.();
    }).catch(error => {
      setIsSubmitting(false);
      enqueueSnackbar(error?.message || 'Unable to submit check-in details', {variant: 'error'});
    });
  };

  const checkInFormFields = (
    <Stack spacing={3}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <DatePicker
          label="Check-In Date"
          format={checkInDateDisplayFormat}
          inputFormat={checkInDateDisplayFormat}
          clearable={false}
          minDate={new Date()}
          value={checkInDateValue}
          onChange={updateCheckInDate}
          renderInput={(params) => (
            <TextField
              {...params}
              required
              fullWidth
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    <InputAdornment position="start">
                      <CalendarMonthIcon color="disabled" />
                    </InputAdornment>
                    {params.InputProps?.startAdornment}
                  </>
                ),
              }}
            />
          )}
          slotProps={{ textField: { fullWidth: true } }}
        />
      </LocalizationProvider>
      <FormControl fullWidth required>
        <InputLabel id="checkin-type-label">Check-In Type</InputLabel>
        <Select
          labelId="checkin-type-label"
          label="Check-In Type"
          value={currentCheckInDetails.checkInType}
          onChange={updateCheckInType}
          displayEmpty
          renderValue={(selected) => selected || 'Select'}
          startAdornment={
            <InputAdornment position="start">
              <HomeWorkIcon color="disabled" />
            </InputAdornment>
          }
        >
          <MenuItem value="" disabled>Select check-in type</MenuItem>
          {CheckInTypes.map(type => (
            <MenuItem value={type} key={type}>{type}</MenuItem>
          ))}
        </Select>
      </FormControl>
      {shouldShowRentDay && (
        <FormControl fullWidth required>
          <InputLabel id="rent-collection-day-label">Rent Collection Day</InputLabel>
          <Select
            labelId="rent-collection-day-label"
            label="Rent Collection Day"
            value={currentCheckInDetails.rentCollectionDay || ''}
            onChange={updateField('rentCollectionDay')}
            displayEmpty
            renderValue={(selected) => selected || 'Select rent collection day'}
            startAdornment={
              <InputAdornment position="start">
                <CalendarMonthIcon color="disabled" />
              </InputAdornment>
            }
          >
            <MenuItem value="" disabled>Select rent collection day</MenuItem>
            {rentCollectionDays.map(day => (
              <MenuItem value={day} key={day}>{day}</MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      {shouldShowDuration && (
        <TextField
          label={durationLabel}
          value={currentCheckInDetails.numberOfWeeksDays || ''}
          onChange={updateNumberOfWeeksDays}
          required
          inputProps={{
            inputMode: 'numeric',
            pattern: '[0-9]*',
            min: 0,
            max: durationMax,
          }}
          helperText={durationMax ? `Maximum allowed: ${durationMax}` : ''}
          fullWidth
        />
      )}
    </Stack>
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h5" fontWeight={700}>{title}</Typography>
          <IconButton onClick={onClose} aria-label="Close confirmation">
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent>
        {isBookingMode ? (
          <>
            <Typography variant="body2" textAlign="center" sx={{ mb: 5, lineHeight: 1.5 }}>
              You already have a booking in this facility for <strong>{getBookingRoomLabel(bookingDetails)}</strong>. {pgDefaults ? rentDetailsMessage : fallbackRentMessage}
            </Typography>
            {checkInFormFields}
          </>
        ) : (
          <>
            <Typography variant="body2" textAlign="center" sx={{ mb: 5, lineHeight: 1.5 }}>
              You have selected room <strong>{selectedRoom?.id}</strong> from <strong>Block {selectedRoom?.block}, Floor {selectedRoom?.floor}</strong>.
              {pgDefaults ? rentDetailsMessage : fallbackRentMessage}
            </Typography>
            {checkInFormFields}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', pb: 4 }}>
        <PrimaryActionButton onClick={submitCheckIn} disabled={isProceedDisabled}>
          {isSubmitting ? 'Submitting...' : proceedLabel || 'Proceed'}
        </PrimaryActionButton>
      </DialogActions>
    </Dialog>
  );
}

export default ConfirmCheckInDialog;
